import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  exportClientTablesMonthly, exportClientTablesQuarterly,
  getClientTablesMonthly, getClientTablesQuarterly,
} from '../api/client'
import { currentLocale } from '../i18n'

/**
 * The per-project, per-engineer tables handed to a client -- one block per
 * project, for a month or a calendar quarter.
 *
 * Admin-only, same as the rate screen it sits beside: the client rate is not
 * a company secret behind a password, it is what the client is about to be
 * billed. Only ACTIVATED project-months ever reach this screen; a project
 * nobody has reviewed yet simply is not here -- go activate it first, in
 * Capacidad > Activacion de horas.
 */
const num = (v, digits = 1) =>
  v == null ? '—' : new Intl.NumberFormat(currentLocale(), {
    maximumFractionDigits: digits }).format(v)

const usd = (v) => (v == null ? '—' : new Intl.NumberFormat(currentLocale(), {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(v))

const quarterOf = (month) => Math.floor((month - 1) / 3) + 1

export default function ClientTables() {
  const { t } = useTranslation(['purchases', 'common'])
  const today = new Date()
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [period, setPeriod] = useState('month')
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  const k = useCallback((suffix, opts) => t(`clientTables.${suffix}`, opts), [t])

  const load = useCallback(() => {
    const fetcher = period === 'month'
      ? getClientTablesMonthly(year, month)
      : getClientTablesQuarterly(year, quarterOf(month))
    fetcher
      .then(d => { setData(d); setError('') })
      .catch(err => setError(err.response?.data?.detail || k('loadFailed')))
  }, [year, month, period, k])

  useEffect(() => { load() }, [load])

  const shift = (delta) => {
    const step = period === 'month' ? 1 : 3
    const d = new Date(year, month - 1 + delta * step, 1)
    setYear(d.getFullYear())
    setMonth(d.getMonth() + 1)
  }

  const label = period === 'month'
    ? new Date(year, month - 1, 1).toLocaleDateString(currentLocale(), {
        month: 'long', year: 'numeric' })
    : `Q${quarterOf(month)} ${year}`

  return (
    <div className="px-8 pb-8 pt-6">
      <div className="mb-6">
        <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">
          {k('title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{k('subtitle')}</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3 mb-4">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          {['month', 'quarter'].map(mode => (
            <button key={mode} onClick={() => setPeriod(mode)}
              className={`px-3 py-1.5 ${period === mode ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k(`period_${mode}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">‹</button>
          <span className="text-sm font-semibold text-gray-900 px-2 capitalize min-w-32 text-center">
            {label}
          </span>
          <button onClick={() => shift(1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">›</button>
        </div>

        <button
          onClick={() => period === 'month'
            ? exportClientTablesMonthly(year, month)
            : exportClientTablesQuarterly(year, quarterOf(month))}
          className="ml-auto bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors">
          {k('export')}
        </button>
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg mb-4">{error}</p>}
      {!data ? (
        <div className="text-gray-400 text-sm">{t('common:actions.loading')}</div>
      ) : data.projects.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">{k('noProjects')}</p>
          <p className="text-xs mt-1">{k('noProjectsHint')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.projects.map(project => (
            <div key={project.project_id}
                 className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {project.code && (
                    <span className="text-xs font-mono bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                      {project.code}
                    </span>
                  )}
                  <span className="font-semibold text-gray-900 text-sm">{project.name}</span>
                </div>
                <span className="text-sm font-bold text-gray-900">{usd(project.total_usd)}</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <th className="text-left font-semibold px-5 py-2">{k('cols.engineer')}</th>
                    <th className="text-right font-semibold px-4 py-2 w-24">{k('cols.hours')}</th>
                    <th className="text-right font-semibold px-4 py-2 w-28">{k('cols.rate')}</th>
                    <th className="text-right font-semibold px-5 py-2 w-32">{k('cols.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {project.engineers.map(eng => (
                    <tr key={eng.team_member_id} className="border-t border-gray-100">
                      <td className="px-5 py-2 text-gray-800">{eng.name}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                        {num(eng.horas)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                        {eng.rate_usd == null ? '—' : usd(eng.rate_usd)}
                      </td>
                      <td className={`px-5 py-2 text-right tabular-nums font-medium ${
                        eng.monto_usd == null ? 'text-amber-600' : 'text-gray-900'}`}>
                        {eng.monto_usd == null ? k('noRate') : usd(eng.monto_usd)}
                      </td>
                    </tr>
                  ))}
                  {project.materials_usd > 0 && (
                    <tr className="border-t border-gray-100 bg-gray-50/60">
                      <td className="px-5 py-2 text-gray-500" colSpan={3}>{k('materials')}</td>
                      <td className="px-5 py-2 text-right tabular-nums text-gray-700">
                        {usd(project.materials_usd)}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t border-gray-200">
                    <td className="px-5 py-2 font-semibold text-gray-700" colSpan={1}>
                      {k('cols.total')}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold text-gray-900">
                      {num(project.horas)}
                    </td>
                    <td />
                    <td className="px-5 py-2 text-right tabular-nums font-bold text-gray-900">
                      {usd(project.total_usd)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}

          {data.missing_rates?.length > 0 && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              {k('missingRates', { names: data.missing_rates.join(', ') })}
            </p>
          )}

          <div className="flex justify-end">
            <span className="text-sm font-semibold text-gray-900">
              {k('grandTotal')}: {usd(data.grand_total_usd)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
